import { useState } from "react";
import { Link, Redirect } from "wouter";
import { useExportCustomerData } from "@orbit/api-client-react";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";

/**
 * /profile/data-export — single button that fetches the full JSON
 * export and triggers a browser download. Synchronous (D33): the
 * server returns the whole payload in one response. We re-derive the
 * filename here and create a Blob → download anchor so the user gets
 * a dated file like `orbit-data-2026-04-27.json`.
 */
export function ProfileDataExportPage() {
  const { isAuthenticated } = useAuth();
  const exportMut = useExportCustomerData();
  const [lastDownloadAt, setLastDownloadAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isAuthenticated) return <Redirect to="/" />;

  async function download() {
    setError(null);
    try {
      const data = await exportMut.mutateAsync();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = data.exportedAt.slice(0, 10);
      a.href = url;
      a.download = `orbit-data-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLastDownloadAt(data.exportedAt);
    } catch (err) {
      const e = err as { apiError?: { message?: string } };
      setError(e.apiError?.message ?? "Couldn't prepare your data export.");
    }
  }

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">Download my data</h1>
        <p className="mt-2 text-sm text-ink-dark">
          Get a copy of everything Orbit knows about you — profile, every
          merchant you've joined, all transactions, vouchers, adjustments,
          and your full consent history. JSON file, machine readable.
        </p>

        <button
          type="button"
          onClick={download}
          disabled={exportMut.isPending}
          className="btn-primary w-full mt-6"
        >
          {exportMut.isPending ? "Preparing…" : "Download my data"}
        </button>

        {error && <p className="mt-4 text-red-600 text-sm">{error}</p>}
        {lastDownloadAt && (
          <p className="mt-4 text-xs text-ink-dark">
            Downloaded {new Date(lastDownloadAt).toLocaleString()}.
          </p>
        )}

        <p className="mt-8 text-xs text-ink-dark/70">
          Under RA 10173 (Data Privacy Act of 2012) you have the right to
          access your personal data at any time.
        </p>
      </div>
    </div>
  );
}
